import { ClerkProvider } from '@clerk/clerk-react';
import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import posthog from 'posthog-js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';

import { getConfig } from './config';
import { InternalServerError } from './error';
import { errorTrackingInit, getErrorTracking, trackError } from './error/tracking';
import './index.css';
import { create as createLogger } from './logger';
import { captureException } from './logger/capture-exception';
import { AppRouter } from './router';
import { getQueryClient } from './sdk/request';
import { initGoogleTagManager } from './services/gtm';
import { initHelpScout } from './services/helpscout';
import { initPostHog } from './services/posthog';
import { prefetchSystemConfigs } from './services/storage/system-config-queries';
import { UIKey } from './services/storage/ui-key-value';
import { i18n, init as initTranslations } from './services/translation';
import { appendUtmQueryString, storeUtmKey } from './shared/utm';

async function init() {
  const config = getConfig();

  // setup the logger...
  createLogger(config.env, true);

  if (config.env !== 'production') {
    // eslint-disable-next-line no-console
    console.log('Config:', config);
  }

  // save any utm source / medium incoming
  storeUtmKey(UIKey.UTM_SOURCE);
  storeUtmKey(UIKey.UTM_MEDIUM);
  storeUtmKey(UIKey.UTM_CAMPAIGN);
  storeUtmKey(UIKey.UTM_TERM);

  // save click IDs from ad platforms
  storeUtmKey(UIKey.GCLID);
  storeUtmKey(UIKey.FBCLID);
  storeUtmKey(UIKey.LI_FAT_ID);

  // setup sentry
  // TODO: Make sure to set the correct Sentry DSN in the environment variables
  errorTrackingInit(config.sentryDsn, { env: config.env });
  const errorTrack = getErrorTracking();

  // initialize GTM
  initGoogleTagManager();

  // initialize HelpScout Beacon
  initHelpScout();

  // setup react / frontend framework
  const rootElement = document.querySelector('#root');
  if (rootElement == null) {
    throw new InternalServerError('no root included in the template file');
  }
  const root = createRoot(rootElement, {
    onUncaughtError: errorTrack.reactErrorHandler(function (error, errorInfo) {
      // eslint-disable-next-line no-console
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    onCaughtError: errorTrack.reactErrorHandler(),
    onRecoverableError: errorTrack.reactErrorHandler(),
  });

  // setup the query client
  const queryClient = getQueryClient();

  initPostHog();

  await initTranslations();
  // Prefetch system configs early (public endpoint, no auth required)
  // This runs in parallel with other data loading
  prefetchSystemConfigs().catch(function (err) {
    // Silently fail - system configs are not critical yet for app initialization
    captureException(
      err,
      {
        file: 'main.tsx',
        method: 'prefetchSystemConfigs',
        infoMessage: 'failed to prefetch system configs',
      },
      trackError
    );
  });

  root.render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <ClerkProvider
            telemetry={{ disabled: true, debug: false }}
            publishableKey={config.clerk.publishableKey}
            // No UTMs on post–sign-out redirect; inbound auth URLs still use appendUtmQueryString below.
            afterSignOutUrl="/auth/signin"
            localization={{
              locale: i18n.language,
            }}
            supportEmail={config.supportEmail}
            signInUrl={appendUtmQueryString('/auth/signin')}
            signUpUrl={appendUtmQueryString('/auth/signup')}
            signInFallbackRedirectUrl={appendUtmQueryString('/auth/wait-user')}
            signUpFallbackRedirectUrl={appendUtmQueryString('/auth/wait-user')}
            signInForceRedirectUrl={appendUtmQueryString('/auth/wait-user')}
            signUpForceRedirectUrl={appendUtmQueryString('/auth/wait-user')}
            allowedRedirectOrigins={[
              config.appDomain,
              'https://app.blym.co',
              'https://app.blym.co/auth',
              'https://app.blym.co/auth/oauthcb',
            ]}
          >
            <I18nextProvider i18n={i18n}>
              <errorTrack.ErrorBoundary showDialog={config.env === 'development'}>
                <AppRouter />
              </errorTrack.ErrorBoundary>
            </I18nextProvider>
          </ClerkProvider>
          <ReactQueryDevtools buttonPosition="bottom-right" initialIsOpen={false} />
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>
  );
}

init().catch(function (err) {
  // TODO: send to sentry
  // eslint-disable-next-line no-console
  console.error(err);
});
